import moment from 'moment-timezone';
import React from 'react';
import useSWR from 'swr';
import { Button, Col, Row, Skeleton, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  DateColumn,
  EmailColumn,
  IndexColumn,
  MembershipLevelColumn,
  SpentAmountColumn,
  TitleColumn,
} from './entered-users-form';
import { TourTrashStatusType, TourTrashType, TourTrashUserType } from '../../../types/tourtrash';
import { API_URLS } from '../../../constants/urls/api-urls';
import {
  PickTourtrashWinnerPayload,
  getTourTrashEnteredUsersRepository,
} from '../../../repository/mytm/tour-trash/tourTrashRepository';
import { TOUR_TRASH_TRANSLATION } from '../../../constants/define/translation';
import { PAGE_SIZE_OPTIONS } from '../../../constants/common';

const { Column } = Table;

interface TourTrashListProps {
  tourTrash: TourTrashType;
  className?: string;
  q?: string;
  perPage: number;
  page: number;
  pickingWinner: boolean;
  handleChangePage: (page: number, pageSize?: number) => void;
  handlePickWinner: (values: PickTourtrashWinnerPayload) => void;
  handleSelectRow: (record: TourTrashUserType) => void;
  selectedRowId?: string;
  disableTab?: boolean;
}

export const UserList = (props: TourTrashListProps) => {
  const { t } = useTranslation(TOUR_TRASH_TRANSLATION);
  const { tourTrash, selectedRowId, handleSelectRow, pickingWinner, handleChangePage, handlePickWinner, disableTab } =
    props;

  const { data, isValidating, error } = useSWR(
    [API_URLS.TOUR_TRASH_ENTERED_USERS, props.page, props.perPage, props.q],
    (url: string, page, perPage, q) => {
      if (disableTab) {
        return undefined;
      }
      return getTourTrashEnteredUsersRepository(url, {
        page,
        take: perPage,
        tourTrashId: tourTrash?.id,
        emailOrName: q.trim(),
      });
    },
    { revalidateOnFocus: false },
  );

  if (!data || error) return <Skeleton />;
  const enteredUsers = (data as any).data.map((user: any, index: any) => ({ ...user, index }));
  const canSelectWinner =
    tourTrash?.status === TourTrashStatusType.ACTIVE &&
    ((selectedRowId && !tourTrash.winner) || (tourTrash.winner && selectedRowId !== tourTrash.winner?.id)) &&
    moment().isAfter(moment(tourTrash.endDate));
  return (
    <>
      <Table
        dataSource={enteredUsers}
        size="small"
        rowClassName={record => (selectedRowId === record.id ? 'selected' : '')}
        loading={isValidating}
        className={props.className}
        rowKey="id"
        onRow={record => {
          return {
            onClick: () => handleSelectRow(record),
          };
        }}
        pagination={{
          showSizeChanger: true,
          onChange: handleChangePage,
          pageSize: props?.perPage,
          current: props?.page,
          total: (data as any).total,
          onShowSizeChange: handleChangePage,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        }}
        scroll={{ y: 400, x: 800 }}
      >
        <Column
          title={t('tab.player.table.title.index')}
          align="center"
          width={70}
          dataIndex="index"
          key="index"
          render={value => <IndexColumn value={value} />}
        />
        <Column
          title={t('tab.player.table.title.name')}
          align="left"
          key="name"
          render={(_, record: TourTrashUserType) => <TitleColumn record={record} />}
        />
        <Column
          title={t('tab.player.table.title.email')}
          align="left"
          key="email"
          render={(_, record: TourTrashUserType) => <EmailColumn record={record} />}
          width="300px"
        />
        <Column
          title={t('tab.player.table.title.level')}
          key="membershipLevel"
          render={(_, record: TourTrashUserType) => <MembershipLevelColumn record={record} />}
        />
        <Column
          title={t('tab.player.table.title.enterDate')}
          key="enteredDate"
          render={(_, record: TourTrashUserType) => <DateColumn date={record?.enteredDate} />}
        />
        <Column
          title={t('tab.player.table.title.spentAmount')}
          key="spentAmount"
          render={(_, record: TourTrashUserType) => <SpentAmountColumn record={record} />}
        />
      </Table>
      <Row align="middle" className="users-pagination">
        <Col span={12}>
          <span className="total-display">
            Show {enteredUsers.length} from {(data as any).total} people
          </span>
        </Col>
      </Row>
      {canSelectWinner && (
        <div className="users-actions">
          <Button
            onClick={() => handlePickWinner({ tourTrashId: tourTrash?.id, userId: selectedRowId! })}
            type="primary"
            disabled={pickingWinner}
            loading={pickingWinner}
          >
            {t('tab.player.table.selectWinner')}
          </Button>
        </div>
      )}
    </>
  );
};
